import React, { useEffect, useState } from 'react';
import { Box, Divider, useTheme } from '@material-ui/core';
import Edit from '@material-ui/icons/Edit';
import EmojiTransportationOutlined from '@material-ui/icons/EmojiTransportationOutlined';
import Error from '@material-ui/icons/Error';
import { Button, IconButton, Panel, Tooltip } from '@michelin/acid-components';
import { useTranslation } from '@michelin/central-provider';
import { AutocompleteSelect } from 'components/SearchSelector';
import { AdminContacts } from 'compositions/OrderDetail/components/AdminContacts';
import { getVehicleLabel } from 'compositions/Wizard/SelectVehicle/VehicleSelection';
import { useCurrentLocationIsDealer } from 'hooks/useCurrentLocationIsDealer';
import { useGoogleAnalytics } from 'hooks/useGoogleAnalytics';
import { AnalyticsAction, AnalyticsCategory } from 'hooks/useGoogleAnalytics/types';
import { VehicleType } from 'types/UFOTypes';
import { useGetVehicles } from '../../hooks/useGetVehicles';
import { VehicleInformation } from './VehicleInformation';
import { VehicleOption } from './VehicleOption';

interface SelectVehicleProps {
  vehicle?: VehicleType | null;
  setVehicle?: (vehicle: VehicleType | null) => void;
  setDisabled?: (value: boolean) => void;
  fleetNumber: string;
  setEditMode?: (value: boolean) => void;
}
export const SelectVehicle = (props: SelectVehicleProps) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const { vehicle, setVehicle = () => {}, setDisabled = () => {}, fleetNumber, setEditMode } = props;

  const haveTireSize = !(!vehicle?.tire_size_id && !vehicle?.custom_tire_size_id);

  useEffect(() => {
    setDisabled(!vehicle || !haveTireSize);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [vehicle]);
  const isDealer = useCurrentLocationIsDealer();
  const [query, setQuery] = useState('');
  const { list: vehiclesList, onSearch: vehicleSearch, loading } = useGetVehicles(fleetNumber);
  const googleAnalyticsUtils = useGoogleAnalytics();

  return (
    <>
      {vehiclesList && (
        <AutocompleteSelect<VehicleType>
          id="vehicle-selector"
          onInputChange={(event: React.ChangeEvent<any>) => {
            if (event?.target?.value) setQuery(event.target.value as string);
          }}
          onBlur={() => setQuery('')}
          noOptionsText={
            query && query.length < 4 && isDealer
              ? t('You have to input at least 4 chars for Licence Plate, VIN, or Unit Number. ({{qty}} chars left)', {
                  qty: 4 - query.length,
                })
              : t('No vehicles found')
          }
          filterOptions={(options) => options}
          style={{ maxWidth: 500, margin: '0 auto' }}
          onSearch={vehicleSearch}
          value={
            vehicle
              ? {
                  value: vehicle.fleet_id_no,
                  data: vehicle,
                  label: getVehicleLabel(vehicle),
                }
              : null
          }
          loading={loading}
          getOptionSelected={(option, value) => option.value === value.value}
          placeholder={
            isDealer
              ? t('Enter VIN, Fleet Unit ID, or License Plate (minimum 4 characters)')
              : t('Search for asset description, unit number, serial number or VIN')
          }
          label={t('Vehicle')}
          onChange={(event: React.ChangeEvent<any>, value) => setVehicle(value?.data ?? null)}
          options={vehiclesList}
          renderOption={VehicleOption}
          popupIcon={isDealer ? <></> : undefined}
        />
      )}
      <Box pt={2} pb={4} style={{ maxWidth: 500, margin: '0 auto', paddingBottom: 0 }}>
        {vehicle && haveTireSize && (
          <Panel dense variant="secondary">
            <VehicleInformation vehicle={vehicle} />
            {setEditMode && (
              <Tooltip title={`${t('Edit vehicle')}`}>
                <IconButton
                  onClick={() => {
                    googleAnalyticsUtils.event({
                      category: AnalyticsCategory.VEHICLE_MANAGEMENT,
                      action: AnalyticsAction.EDIT_VEHICLE,
                      isDealer: !!isDealer,
                    });
                    setEditMode(true);
                  }}
                  color="primary"
                  style={{
                    position: 'absolute',
                    top: 0,
                    right: 0,
                  }}
                >
                  <Edit />
                </IconButton>
              </Tooltip>
            )}
          </Panel>
        )}
        {!vehicle && (
          <Panel dense variant="secondary">
            <div
              style={{
                textAlign: 'center',
                maxWidth: 300,
                margin: 'auto',
                color: theme.palette.secondary.dark,
              }}
            >
              <EmojiTransportationOutlined fontSize="large" />
              <br />
              <span style={{ fontSize: 18 }}>
                <b>{t('No vehicle selected')}</b>
              </span>
              <br />
              {t('Please select a vehicle')}
            </div>
          </Panel>
        )}
        {vehicle && !haveTireSize && (
          <Panel dense variant="secondary">
            <VehicleInformation vehicle={vehicle} />
            <Divider style={{ marginTop: 16, marginBottom: 16 }} />
            <div style={{ textAlign: 'center', margin: 'auto' }}>
              <span style={{ fontSize: 18 }}>
                <Error style={{ marginBottom: -6, color: theme.palette.warning.main, marginRight: 4 }} />
                <b>{t('Missing Option Package and Tire Size.')}</b>
              </span>
              <br />
              {setEditMode && (
                <Button
                  color="primary"
                  onClick={() => {
                    googleAnalyticsUtils.event({
                      category: AnalyticsCategory.VEHICLE_MANAGEMENT,
                      action: AnalyticsAction.ADD_MISSING_INFORMATION,
                      isDealer: !!isDealer,
                    });
                    setEditMode(true);
                  }}
                  size="small"
                  style={{ marginTop: 16 }}
                  fullWidth
                >
                  {t('Add Missing Vehicle Information')}
                </Button>
              )}
              {!setEditMode && (
                <>
                  {t('Please contact the fleet administrator to update the vehicle information.')}
                  <Panel
                    dense
                    variant="secondary"
                    style={{
                      height: 264,
                      marginTop: 16,
                      overflowY: 'scroll',
                      overflowX: 'hidden',
                      textAlign: 'left',
                    }}
                  >
                    <AdminContacts fleetId={fleetNumber} />
                  </Panel>
                </>
              )}
            </div>
          </Panel>
        )}
      </Box>
    </>
  );
};
